import { useEffect, useState } from 'react';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { TextInput } from '../../../components/TextInput';
import type { CreateStripeCustomerRequest } from '../../../types/adminBilling';

interface StripeCustomerModalProps {
  isOpen: boolean;
  /** Prefills the name, because it is the one detail this system already holds. */
  organizationName: string;
  saving: boolean;
  error: string | null;
  onCreate: (request: CreateStripeCustomerRequest) => void;
  onClose: () => void;
}

/** Trimmed, or undefined when there is nothing there. */
const filled = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * The details a new Stripe customer is created from.
 *
 * **An empty field is not sent.** Omitting one means "use what this system already knows" - the
 * organization's own name, the address its invoices already go to - and everything else has no
 * column anywhere here, so sending a blank string would store a blank at Stripe rather than leaving
 * the field unset. That is why every value goes through `filled` and why the address is only
 * attached when some part of it was typed.
 *
 * **It describes a customer this call CREATES.** On an organization that already has a live Stripe
 * customer the same button links to that one and applies none of this, which is what the sentence
 * the server hands back says. Nothing is guessed about that here; the response is shown as written.
 */
export const StripeCustomerModal = ({
  isOpen,
  organizationName,
  saving,
  error,
  onCreate,
  onClose,
}: StripeCustomerModalProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [taxId, setTaxId] = useState('');
  const [taxIdType, setTaxIdType] = useState('');
  const [countryError, setCountryError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(organizationName);
    setEmail('');
    setPhone('');
    setDescription('');
    setLine1('');
    setLine2('');
    setCity('');
    setState('');
    setPostalCode('');
    setCountry('');
    setTaxId('');
    setTaxIdType('');
    setCountryError(null);
  }, [isOpen, organizationName]);

  const handleConfirm = () => {
    const countryCode = filled(country)?.toUpperCase();

    // Checked here as well as at the API, because it is the one field whose shape an operator
    // cannot guess: "United States" is refused with a 400 and a round trip otherwise.
    if (countryCode !== undefined && !/^[A-Z]{2}$/.test(countryCode)) {
      setCountryError('Country must be a two letter code, such as US.');
      return;
    }
    setCountryError(null);

    const address = {
      ...(filled(line1) ? { line1: filled(line1) } : {}),
      ...(filled(line2) ? { line2: filled(line2) } : {}),
      ...(filled(city) ? { city: filled(city) } : {}),
      ...(filled(state) ? { state: filled(state) } : {}),
      ...(filled(postalCode) ? { postalCode: filled(postalCode) } : {}),
      ...(countryCode ? { country: countryCode } : {}),
    };

    onCreate({
      ...(filled(name) ? { name: filled(name) } : {}),
      ...(filled(email) ? { email: filled(email) } : {}),
      ...(filled(phone) ? { phone: filled(phone) } : {}),
      ...(filled(description) ? { description: filled(description) } : {}),
      ...(Object.keys(address).length > 0 ? { address } : {}),
      ...(filled(taxId) ? { taxId: filled(taxId) } : {}),
      ...(filled(taxIdType) ? { taxIdType: filled(taxIdType) } : {}),
    });
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Create a Stripe customer"
      message={`Creates the customer "${organizationName}" is billed as at Stripe, and hands its billing to Stripe from then on.`}
      confirmText={saving ? 'Creating...' : 'Create customer'}
      cancelText="Cancel"
      busy={saving}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="billing-form">
        <p className="billing-form__hint">
          Both of these fall back to what this system already knows: the name to this
          organization&apos;s own, and the email to its first active To billing contact, which is
          where its invoices already go. Creating with both blank is fine.
        </p>
        <TextInput
          label="Customer name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-name"
        />
        <TextInput
          label="Invoice email (optional)"
          type="email"
          placeholder="Uses the billing contact"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-email"
        />
        {/*
          Everything below is optional and Stripe is simply not told about a blank, so the default
          form is the two fields that matter. Twelve inputs, eleven of them marked optional, read as
          a dozen decisions to make before a customer can exist - and the address and tax id are
          things a college's finance office supplies later, if ever.
        */}
        <details className="billing-form__details">
          <summary className="billing-form__summary">
            Billing details for the invoice (optional)
          </summary>

        <TextInput
          label="Phone (optional)"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-phone"
        />
        <TextInput
          label="Description (optional)"
          placeholder="Whoever reads this in the Stripe dashboard"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-description"
        />
        <TextInput
          label="Address line 1 (optional)"
          value={line1}
          onChange={(e) => setLine1(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-line1"
        />
        <TextInput
          label="Address line 2 (optional)"
          value={line2}
          onChange={(e) => setLine2(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-line2"
        />
        <TextInput
          label="City (optional)"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-city"
        />
        <TextInput
          label="State or province (optional)"
          value={state}
          onChange={(e) => setState(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-state"
        />
        <TextInput
          label="Postal code (optional)"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-postal"
        />
        <TextInput
          label="Country (optional)"
          placeholder="US"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          error={countryError ?? undefined}
          disabled={saving}
          data-testid="billing-stripe-customer-country"
        />
        <TextInput
          label="Tax id (optional)"
          placeholder="An EIN, for a US college"
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-tax-id"
        />
        <TextInput
          label="Tax id type (optional)"
          placeholder="us_ein"
          value={taxIdType}
          onChange={(e) => setTaxIdType(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-customer-tax-id-type"
        />
        <p className="billing-form__hint">
          A tax id needs a type. Leave the type blank with an id filled in and Stripe is told it is
          a US EIN, which is what a US college has. The address is what prints on the invoice.
        </p>
        </details>
        {error && (
          <p className="billing-form__error" role="alert" data-testid="billing-stripe-customer-error">
            {error}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
};
